import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiGlobe, FiCheck } from 'react-icons/fi';
import { SUPPORTED_LANGUAGES, getLanguageMeta } from '../../i18n/supportedLanguages';
import { toMultilangMap, normalizeMultilang } from '../../i18n/getLocalized';
import './MultiLangInput.scss';

/**
 * Text input that supports multiple languages. The user enters text in the
 * primary language inline; clicking the globe icon reveals additional inputs
 * for every supported language. The stored value is a plain string when only
 * one language is filled in, or `{ en, tr, ... }` when several are set.
 *
 * Props:
 *   value          — string | { [lang]: string }
 *   onChange       — (newValue: string | object) => void
 *   primaryLang    — language code to show on the inline input (default: 'en')
 *   languages      — list of supported language codes (default: SUPPORTED_LANGUAGES)
 *   placeholder    — placeholder for inline input
 *   multiline      — render <textarea> instead of <input>
 *   rows           — when multiline, sets rows attr (default 2)
 *   className      — extra class for the outer wrapper
 *   inputClassName — extra class for the actual input/textarea
 */
function MultiLangInput({
  value,
  onChange,
  primaryLang = 'en',
  languages = SUPPORTED_LANGUAGES,
  placeholder = '',
  multiline = false,
  rows = 2,
  className = '',
  inputClassName = '',
  disabled = false,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState('bottom');
  // Viewport coordinates for the popover. It is rendered through a portal so
  // that scrollable/overflow-hidden ancestors (modals, cards, the form-builder
  // canvas) cannot clip it.
  const [coords, setCoords] = useState(null);
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  const map = useMemo(() => toMultilangMap(value, primaryLang), [value, primaryLang]);
  const filledCount = languages.reduce((n, c) => n + (map[c]?.trim() ? 1 : 0), 0);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      // The popover lives outside this subtree, so it needs its own hit test.
      if (wrapRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Position the popover against the input, flipping above when space is tight.
  useEffect(() => {
    if (!open) { setCoords(null); return undefined; }
    const recalc = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const popHeight = popRef.current?.offsetHeight || 320;
      const margin = 12;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const flip = spaceBelow < popHeight + margin && spaceAbove > spaceBelow;
      setPlacement(flip ? 'top' : 'bottom');

      // Match the input's width, but keep the popover readable and on-screen.
      const width = Math.min(560, Math.max(320, rect.width));
      let left = rect.right - width;          // right-aligned to the input
      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));

      setCoords({
        left,
        width,
        ...(flip
          ? { bottom: window.innerHeight - rect.top + 8 }
          : { top: rect.bottom + 8 }),
      });
    };
    recalc();
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [open]);

  const update = (lang, text) => {
    const next = { ...map, [lang]: text };
    onChange(normalizeMultilang(next, primaryLang));
  };

  const InputEl = multiline ? 'textarea' : 'input';

  return (
    <div className={`multilang ${className}`} ref={wrapRef}>
      <div className="multilang__row">
        <InputEl
          className={`multilang__input ${inputClassName}`}
          value={map[primaryLang] || ''}
          onChange={(e) => update(primaryLang, e.target.value)}
          placeholder={placeholder}
          rows={multiline ? rows : undefined}
          disabled={disabled}
        />
        <button
          type="button"
          className={`multilang__btn ${filledCount > 1 ? 'multilang__btn--active' : ''}`}
          onClick={() => setOpen((o) => !o)}
          aria-label={t('multilang.addTranslations')}
          aria-expanded={open}
          disabled={disabled}
          title={filledCount > 1
            ? t('multilang.translationCount', { count: filledCount })
            : t('multilang.addTranslations')}
        >
          <FiGlobe />
          {filledCount > 1 && <span className="multilang__count">{filledCount}</span>}
        </button>
      </div>

      {open && coords && createPortal(
        <div
          ref={popRef}
          className={`multilang__pop multilang__pop--portal multilang__pop--${placement}`}
          style={{
            position: 'fixed',
            left: coords.left,
            width: coords.width,
            top: coords.top,
            bottom: coords.bottom,
          }}
        >
          <div className="multilang__pop-head">
            <FiGlobe />
            <span>{t('multilang.translations')}</span>
          </div>
          <div className="multilang__pop-body">
            {languages.map((code) => {
              const meta = getLanguageMeta(code);
              const filled = !!(map[code] && map[code].trim());
              return (
                <div key={code} className="multilang__lang">
                  <div className="multilang__lang-head">
                    <span className="multilang__lang-code">{meta.flag || code.toUpperCase()}</span>
                    <span className="multilang__lang-name">{meta.nativeLabel || meta.label}</span>
                    {filled && <FiCheck className="multilang__lang-check" />}
                  </div>
                  <InputEl
                    className="multilang__input multilang__input--pop"
                    value={map[code] || ''}
                    onChange={(e) => update(code, e.target.value)}
                    placeholder={placeholder}
                    rows={multiline ? rows : undefined}
                  />
                </div>
              );
            })}
          </div>
          <div className="multilang__pop-foot">
            {t('multilang.fallbackHint')}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default MultiLangInput;
